// Use proxy in development, full URL in production
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_BASE_URL || 'https://api.afriqcm.com';
  }
  // In development, use proxy (no origin needed)
  return '';
};

const API_BASE_URL = getApiBaseUrl();
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';
const BLOG_API_URL = API_BASE_URL ? `${API_BASE_URL}/${API_VERSION}/blogs` : `/${API_VERSION}/blogs`;

const handleApiResponse = async (response) => {
  const data = await response.json();
  if (data.status === 'success') {
    return data;
  } else {
    throw new Error(data.message || 'API error');
  }
};

const handleApiError = (error) => {
  console.error('API call failed:', error);
  throw new Error(error.message || 'An unexpected error occurred.');
};

export const getBlogs = async (params = {}) => {
  try {
    const defaultParams = {
      skip: 0,
      limit: 6,
      status_filter: 'PUBLISHED',
      ...params
    };
    
    const queryString = new URLSearchParams(defaultParams).toString();
    const url = `${BLOG_API_URL}/?${queryString}`;
    
    console.log('Fetching blogs from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
      },
      mode: 'cors', // Explicitly set CORS mode
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await handleApiResponse(response);
    console.log('Blogs fetched successfully:', data);
    return data;
  } catch (error) {
    console.error('Fetch error details:', error);
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('CORS error: Unable to connect to API. Please ensure CORS is enabled on the API server.');
    }
    handleApiError(error);
    throw error;
  }
};

export const getBlogById = async (blogId) => {
  try {
    const url = `${BLOG_API_URL}/${blogId}`;
    console.log('getBlogById: Fetching from URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
      },
    });

    console.log('getBlogById: Response status:', response.status);
    console.log('getBlogById: Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('getBlogById: Response error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await handleApiResponse(response);
    console.log('getBlogById: Successfully fetched blog:', data);
    return data;
  } catch (error) {
    console.error('getBlogById: Error details:', error);
    handleApiError(error);
    throw error;
  }
};

export const trackBlogView = async (blogId) => {
  try {
    const response = await fetch(`${BLOG_API_URL}/${blogId}/view`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // Don't throw error for view tracking - it's not critical
      console.warn(`Failed to track blog view for blog ${blogId}:`, response.status);
      return null;
    }

    return await handleApiResponse(response);
  } catch (error) {
    // Silently fail for view tracking - don't interrupt user experience
    console.warn('Error tracking blog view:', error);
    return null;
  }
};

export const getBlogComments = async (blogId, params = {}) => {
  try {
    const defaultParams = {
      skip: 0,
      limit: 20,
      ...params
    };
    
    const queryString = new URLSearchParams(defaultParams).toString();
    const url = `${BLOG_API_URL}/${blogId}/comments?${queryString}`;
    console.log('getBlogComments: Fetching from URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
      },
    });

    console.log('getBlogComments: Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('getBlogComments: Response error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await handleApiResponse(response);
    console.log('getBlogComments: Successfully fetched comments:', data);
    return data;
  } catch (error) {
    console.error('getBlogComments: Error details:', error);
    handleApiError(error);
    throw error;
  }
};

export const submitComment = async (blogId, commentData) => {
  try {
    const url = `${BLOG_API_URL}/${blogId}/comment`;
    console.log('submitComment: Submitting to URL:', url);
    console.log('submitComment: Comment data:', commentData);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(commentData),
    });

    console.log('submitComment: Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('submitComment: Response error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await handleApiResponse(response);
    console.log('submitComment: Successfully submitted comment:', data);
    return data;
  } catch (error) {
    console.error('submitComment: Error details:', error);
    handleApiError(error);
    throw error;
  }
};

